/**
 * VersionManager Component
 * Handles two scenarios:
 * 1. Chunk load errors (reactive) - Prompts user to refresh when JS chunks fail to load
 * 2. Version mismatch (proactive) - Polls /api/build-id to detect new deployments
 */

'use client';

import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID;
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

export function VersionManager() {
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    // Handle chunk load errors - detects when user has stale cached code
    const handleChunkError = (event: Event) => {
      // ErrorEvent has message property, regular Error events don't
      const errorEvent = event as ErrorEvent;
      const message = errorEvent.message || '';
      const error = errorEvent.error;
      const errorMessage = error?.message || '';

      const isChunkError =
        message.includes('Failed to load chunk') ||
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError') ||
        errorMessage.includes('Failed to load chunk') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError') ||
        error?.name === 'ChunkLoadError';

      if (isChunkError) {
        event.preventDefault();
        setBannerMessage('Se detectó un problema al cargar la aplicación.');
        setShowRefreshBanner(true);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason?.message || String(reason) || '';

      const isChunkError =
        message.includes('Failed to load chunk') ||
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError');

      if (isChunkError) {
        event.preventDefault();
        setBannerMessage('Se detectó un problema al cargar la aplicación.');
        setShowRefreshBanner(true);
      }
    };

    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    // Version check via API endpoint - only in production with a build ID
    if (process.env.NODE_ENV !== 'production' || !BUILD_ID) return;

    const checkVersion = async () => {
      try {
        const response = await fetch('/api/build-id', {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.ok) return;

        const data = await response.json();
        const serverBuildId = data.buildId;

        if (serverBuildId && serverBuildId !== BUILD_ID) {
          setBannerMessage('Hay una nueva versión disponible.');
          setShowRefreshBanner(true);
        }
      } catch {
        // Silently fail version checks - not critical
      }
    };

    // Check periodically
    const timer = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Initial check after a delay (don't check immediately on load)
    const initialCheck = setTimeout(checkVersion, 30000);

    return () => {
      clearInterval(timer);
      clearTimeout(initialCheck);
    };
  }, []);

  if (!showRefreshBanner) return null;

  return (
    <div className="fixed top-0 right-0 left-0 z-[9999] bg-gradient-to-r from-cyan-600 to-cyan-700 px-4 py-3 text-white shadow-lg">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 animate-pulse" />
          <p className="text-sm font-medium">
            {bannerMessage} Por favor recarga la página para continuar.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="shrink-0 rounded-lg bg-white/20 px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-white/30"
        >
          Recargar ahora
        </button>
      </div>
    </div>
  );
}
